import { Component, Input, Output, EventEmitter, OnInit, ViewChildren, QueryList } from '@angular/core';
import { Customer } from '../../../models/customer/customer';
import { CustomerService } from '../../../models/customer/customerService';
import { TextModes } from '../../ui/controls/text-input/text-input.component';
import { UIErrorHandler } from '../../../core/uiErrorHandler';
import { TokenizeDialogComponent } from '../../tokenize/tokenize-dialog/tokenize-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { PaymentMethod } from '../../../models/paymentMethod/paymentMethod';
import { PaymentMethodService } from '../../../models/paymentMethod/paymentMethodService';
import { ProcessorType } from '../../../core/constants';
import { Textbox } from '../../../../../../../libs/Shared/components/inputs/textbox/textbox';
import { serializablePropsAreEqual } from '../../../core/type-helpers';
import { UIHelpers } from '../../../core/ui-helpers';
import { ListItem } from '../../../../../../../libs/Shared/components/model/listItem';
import { AccessManager } from '../../ui/access-manager/access-manager';

export type AddCustomerMode = 'add' | 'edit';
@Component({
  selector: 'app-add-customer',
  templateUrl: './add-customer.component.html',
  standalone: false,
})
export class AddCustomerComponent implements OnInit {
  @Input() customer: Partial<Customer> = Customer.createEmpty();
  @Input() merchantId: string | undefined;
  @Input() hideCancel: boolean = false;
  @Input() mode: AddCustomerMode = 'add';
  @Output() newCustomer = new EventEmitter<Partial<Customer>>();

  pageAccessBit: string = AccessManager.AUTH_UPDATE_MERCHANT_CUSTOMERS;

  inputTextModes = TextModes;
  ProcessorType = ProcessorType;
  customerValid = false;
  customerChanged = false;
  updatingCustomer = false;

  selectedPaymentType: ProcessorType | null = null;
  paymentMethods: PaymentMethod[] = [];
  paymentTypeOptions: ListItem<ProcessorType>[] = [new ListItem({ id: ProcessorType.Card, name: 'Credit Card' }), new ListItem({ id: ProcessorType.ACH, name: 'ACH Transfer' })];

  // Since we are emitting a new customer record on insert or update,
  // we don't bind to the original data.  This prevents a lot of
  // checks for valid, too.
  boundCustomer: Partial<Customer> = Customer.createEmpty();
  cachedCustomer: Partial<Customer> = Customer.createEmpty();
  @ViewChildren(Textbox) textInputs!: QueryList<Textbox>;

  constructor(
    private customerService: CustomerService,
    public router: Router,
    public addPaymentMethodDialog: MatDialog,
    public paymentMethodService: PaymentMethodService,
    private uiErrorHandler: UIErrorHandler,
  ) {
    
  }


  ngOnInit() {

    this.customerValid = this.mode == 'edit';
    if (this.customer) this.setCustomer(this.customer);
    else if (this.merchantId) this.boundCustomer.merchantId = this.merchantId;
    else throw 'No merchant or customer selected.';
  }

  checkCustomerValid() {
    this.customerChanged = !serializablePropsAreEqual(this.boundCustomer, this.cachedCustomer, ['firstName', 'lastName', 'emailAddress', 'phone'])
    this.customerValid = this.textInputs.toArray()
      .map(control => control.checkValiditySilently())
      .every(isValid => isValid);
  }

  async handleTokenized(tokenizedData: any) {
    this.loadPaymentMethods();
    this.selectedPaymentType = null;
  }

  btnNewPaymentMethod_click() {
    let ref = this.addPaymentMethodDialog.open(TokenizeDialogComponent, {
      //width: '500px',
      disableClose: false,
      data: {
        merchantId: this.merchantId,
        customerId: this.boundCustomer?.id,
        paymentType: 'Card',
      },
    });

    this.router.events.subscribe(() => {
      ref.close();
    });

    ref.afterClosed().subscribe((paymentMethodId) => {
      if (paymentMethodId != undefined && paymentMethodId != '') {
        this.loadPaymentMethods();
      }
    });
  }

  async loadPaymentMethods() {
    console.log('loadPaymentMethods', this.boundCustomer);
    if (this.boundCustomer?.id) {
      await this.paymentMethodService
        .getPaymentMethodsByCustomerId(this.boundCustomer?.id)
        .then((pms) => {
          this.paymentMethods = pms;
          this.boundCustomer.paymentMethods = pms;
          this.setCustomer(this.boundCustomer);
        })
        .catch((message) => {
          this.uiErrorHandler.handleError(message);
        });
    }
  }

  async saveCustomer() {
    this.updatingCustomer = true;
    if (this.boundCustomer.phone) {
      this.boundCustomer.phone = this.boundCustomer.phone?.replace(/\-/g, '');
    }

    if (!this.merchantId || this.merchantId == '') {
      throw 'merchant id required on add-customer form';
    }
    try {
      if (this.boundCustomer?.id && this.boundCustomer.id.trim() != '') {
        // update customer
        await this.customerService.patchCustomerContactInfo(this.boundCustomer)
        this.setCustomer(this.boundCustomer);
      } else {
        const customer = await this.customerService.createCustomer(this.boundCustomer);
        console.log('customer', customer);
        this.setCustomer(customer);
        this.mode = 'edit';
      } 
    } catch (error) {
      this.uiErrorHandler.handleError(error);
    }
    this.updatingCustomer = false;
    this.customerChanged = false;
  }

  btnCancel_click() {
    this.newCustomer.emit(undefined);
  }

  deletePaymentMethod(paymentMethodId: string) {
    if (confirm('Are you sure you want to delete this payment method?')) {
      this.paymentMethodService
        .deletePaymentMethod(paymentMethodId)
        .then(async() => {
          this.loadPaymentMethods();
        })
        .catch((message) => {
          this.uiErrorHandler.handleError(message);
        });
    }
  }

  setCustomer(customer: Partial<Customer>) {
    this.boundCustomer = (customer as Customer).clone();
    this.cachedCustomer = (customer as Customer).clone();
    this.paymentMethods = customer.paymentMethods || [];
  }

  getPaymentMethods() {
    return this.paymentMethods;
  }
}
